import { useProjectMcpSession } from "@/lib/hooks/useProjectMcpSession";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import {
  BarChart,
  // BarChart3,
  Cloud,
  LayoutDashboard,
  MessageSquare,
  Search,
} from "lucide-react";

interface ProjectTabsProps {
  projectId: string;
  activeTab: string;
}

const tabs = [
  {
    id: "overview",
    label: "Overview",
    icon: LayoutDashboard,
    description: "Smart project dashboard with context-aware tools",
  },
  {
    id: "inspector",
    label: "Inspector",
    icon: Search,
    description: "Full-featured MCP tools and resources inspector",
  },
  {
    id: "debug-chat",
    label: "Chat",
    icon: MessageSquare,
    description: "Advanced MCP chat with debugging tools",
  },
  {
    id: "observability",
    label: "Observability",
    icon: BarChart,
    description: "Performance analytics, traces, and server statistics",
  },
  {
    id: "deploys",
    label: "Deploys",
    icon: Cloud,
    description: "Deployment management with status tracking and logs",
  },
] as const;

export function ProjectTabs({ projectId, activeTab }: ProjectTabsProps) {
  useProjectMcpSession(projectId);

  return (
    <nav className="flex space-x-8" role="tablist">
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;

        return (
          <Link
            key={tab.id}
            to="/projects/$projectId"
            params={{ projectId }}
            search={{ tab: tab.id }}
            role="tab"
            aria-selected={isActive}
            aria-controls={`tabpanel-${tab.id}`}
            className={cn(
              "group flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-all duration-200",
              "hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-t-md",
              isActive
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:border-muted-foreground",
            )}
          >
            <Icon
              className={cn(
                "w-4 h-4 transition-colors",
                isActive
                  ? "text-primary"
                  : "text-muted-foreground group-hover:text-foreground",
              )}
            />
            <span>{tab.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

// Export the tab definitions for use in other components
export { tabs as projectTabs };
export type ProjectTab = (typeof tabs)[number]["id"];
